import React, { useRef } from 'react'
import { FormFieldTextBelow } from '@ds/components/forms/FormField'
import { HelpTextProps } from '@ds/components/forms/FormField/interfaces'
import cx from 'classnames'
import { Icon, l } from '@manychat/manyui'

import { useKnowledgeEditorContext } from 'apps/aiAutomation/components/KnowledgeEditor/lib/context'
import MCTextEditor, {
  IMCTextEditorProps,
  IMCTextEditorRef,
} from 'components/MCTextEditor/MCTextEditor'

import { useEditorScroll } from './hooks/useEditorScroll'
import { useHighlight } from './hooks/useHighlight'

import cm from './styles.module.css'

interface SourceTextEditorProps extends IMCTextEditorProps {
  label: string
  readOnly?: boolean
  value?: string
}

export const SourceTextEditor = ({
  readOnly = false,
  value = '',
  invalid,
  helpText,
  errorText,
  className,
  label,
  ...otherProps
}: SourceTextEditorProps & HelpTextProps) => {
  const mcEditorRef = useRef<IMCTextEditorRef | null>(null)

  const { highlightedChunks, selectedChunkId } = useKnowledgeEditorContext()
  const { editorValue, filterPasteSlices, customProcessor } = useHighlight({
    value,
    highlightedChunks,
  })

  const { editorWrapperRef, onHighlightClick } = useEditorScroll({
    highlightedChunks,
  })

  const focusOnEditor = () => {
    if (!mcEditorRef.current) {
      return
    }

    mcEditorRef.current.focus()
  }

  return (
    <div className={cx(cm.wrapper, className)}>
      <div className="d-flex justify-between">
        <h3 className="text-body font-medium m-t-0">{label}</h3>
        {highlightedChunks.length > 0 && (
          <div className="d-flex justify-between gap-xs align-center">
            <div className={cm.chunks__icon}>
              <Icon.Ai size={11} />
            </div>
            <p className="m-t-0 font-medium">{l.translate('Answer based on:')}</p>
            <div className="d-flex gap-xxs">
              {highlightedChunks.map(({ id }, index) => (
                <button
                  type="button"
                  className={cx(
                    cm.chunks__button,
                    selectedChunkId === id && cm.chunks__buttonActive,
                  )}
                  key={id}
                  onClick={() => onHighlightClick(id)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div
        className={cx(
          cm.editorWrapper,
          !readOnly && cm.editorWrapper__editable,
          invalid && cm.editorWrapper__invalid,
          Boolean(helpText || errorText) && 'm-b-xs',
        )}
        onClick={focusOnEditor}
        ref={editorWrapperRef}
      >
        <MCTextEditor
          {...otherProps}
          disabled={readOnly}
          value={editorValue}
          ref={mcEditorRef}
          noDelay
          customProcessor={customProcessor}
          filterPasteSlices={filterPasteSlices}
          mergeSlicesOnPaste
        />
      </div>
      {!readOnly && (
        <FormFieldTextBelow helpText={helpText} errorText={errorText} showError={invalid} />
      )}
    </div>
  )
}
